import * as THREE from "three";
import { useEffect, useRef, useState } from "react";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { VRButton } from "three/examples/jsm/webxr/VRButton.js";
import { ARButton } from "three/examples/jsm/webxr/ARButton.js";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import pako from "pako";

export default function Viewer() {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const socketRef = useRef(null);

  const [hudData, setHudData] = useState({
    turbine: 0,
    solar: 0,
    consumption: 0,
    net: 0,
  });

  const [hudHistory, setHudHistory] = useState([]);
  const [hoverKey, setHoverKey] = useState(null);

  // Grafik için geçmiş
  useEffect(() => {
    setHudHistory((prev) => {
      const next = [...prev, { ...hudData }];
      if (next.length > 50) next.shift();
      return next;
    });
  }, [hudData]);

  useEffect(() => {
    if (rendererRef.current) return;

    // === SCENE ===
    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.01,
      100
    );
    camera.position.set(8, 5, 12);

    // === RENDERER ===
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.xr.enabled = true;
    renderer.xr.setReferenceSpaceType("local-floor");
    rendererRef.current = renderer;

    const mountNode = mountRef.current; // cleanup-safe kopya
    mountNode.innerHTML = "";
    mountNode.appendChild(renderer.domElement);

    // === DOM OVERLAY ROOT (AR jestleri ve XR butonu için güvenli alan) ===
    let overlayRoot = document.getElementById("xr-overlay-root");
    if (!overlayRoot) {
      overlayRoot = document.createElement("div");
      overlayRoot.id = "xr-overlay-root";
      Object.assign(overlayRoot.style, {
        position: "fixed",
        inset: "0",
        zIndex: "10000",
        pointerEvents: "none", // AR başladığında "auto"ya alınacak
      });
      document.body.appendChild(overlayRoot);
    }

    let model = null;
    let hitTestSource = null;
    let localSpace = null;
    let placed = false;

    // === LIGHTS ===
    const ambient = new THREE.AmbientLight(0xffffff, 0.8);
    const keyLight = new THREE.DirectionalLight(0xffffff, 1);
    keyLight.position.set(5, 10, 7);
    scene.add(ambient, keyLight);

    // === MODEL ===
    const loader = new GLTFLoader();
    loader.load(
      process.env.PUBLIC_URL + "/models/power_plant.glb",
      (gltf) => {
        model = gltf.scene;
        model.scale.set(0.05, 0.05, 0.05);
        model.visible = true; // desktop'ta direkt göster
        scene.add(model);
      }
    );

    // === CONTROLS (desktop/VR) ===
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    // === XR BUTTON tekilleştirici ===
    const ensureXRButton = (isAR) => {
      const old = document.getElementById("XRButton");
      if (old) old.remove();
      const btn = isAR
        ? ARButton.createButton(renderer, {
            requiredFeatures: ["hit-test"],
            optionalFeatures: ["dom-overlay"],
            domOverlay: { root: overlayRoot },
          })
        : VRButton.createButton(renderer);
      btn.id = "XRButton";
      Object.assign(btn.style, {
        position: "fixed",
        left: "20px",
        bottom: "20px",
        zIndex: "10001",
        pointerEvents: "auto",
        display: "block",
        visibility: "visible",
        opacity: "1",
      });
      overlayRoot.appendChild(btn);
      return btn;
    };

    // === DETECT XR CAPABILITY ===
    if (navigator.xr) {
      navigator.xr.isSessionSupported("immersive-ar").then((supported) => {
        ensureXRButton(!!supported);
        if (supported) {
          // AR görünüm varsayılanı: saydam
          renderer.setClearColor(0x000000, 0);
          scene.background = null;
          scene.fog = null;
        } else {
          // VR/desktop görünüm
          renderer.setClearColor(0x000000, 1);
          scene.background = new THREE.Color("#000010");
          scene.fog = new THREE.Fog(new THREE.Color(0x001020), 4, 20);
        }
      });
    } else {
      // Desktop (no XR)
      renderer.setClearColor(0x000000, 1);
      scene.background = new THREE.Color("#000010");
      scene.fog = new THREE.Fog(new THREE.Color(0x001020), 4, 20);
    }

    // === WEBSOCKET ===
    const url = "wss://websocket.biberci.tr/facility/68750198697be316f44e";
    const connectSocket = () => {
      const socket = new WebSocket(url);
      socket.binaryType = "arraybuffer";
      socketRef.current = socket;

      socket.onopen = () => console.log("✅ WebSocket connected");
      socket.onclose = () => {
        console.warn("⚠️ Disconnected, reconnecting in 5s...");
        setTimeout(connectSocket, 5000);
      };
      socket.onerror = (err) => console.error("❌ WebSocket error:", err);

      socket.onmessage = async (event) => {
        let text = "";
        try {
          if (typeof event.data === "string") {
            text = event.data;
          } else if (event.data instanceof ArrayBuffer) {
            const uint = new Uint8Array(event.data);
            try {
              text = pako.inflate(uint, { to: "string" });
            } catch {
              text = new TextDecoder("utf-8").decode(uint);
            }
          } else if (event.data instanceof Blob) {
            const buf = await event.data.arrayBuffer();
            const uint = new Uint8Array(buf);
            try {
              text = pako.inflate(uint, { to: "string" });
            } catch {
              text = new TextDecoder("utf-8").decode(uint);
            }
          }

          const msg = JSON.parse(text);
          const e = msg.energy || msg;

          setHudData({
            turbine: parseFloat(e.turbine_production ?? 0),
            solar: parseFloat(e.solar_production ?? 0),
            consumption: parseFloat(e.internal_consumption ?? 0),
            net: parseFloat(e.net_production ?? 0),
          });
        } catch (err) {
          console.warn("❌ Parse fail:", err);
        }
      };
    };
    connectSocket();

    // === AR HIT TEST göstergesi ===
    const reticleGeo = new THREE.RingGeometry(0.1, 0.15, 32).rotateX(-Math.PI / 2);
    const reticleMat = new THREE.MeshBasicMaterial({ color: 0x00ffcc });
    const reticle = new THREE.Mesh(reticleGeo, reticleMat);
    reticle.matrixAutoUpdate = false;
    reticle.visible = false;
    scene.add(reticle);

    // Desktop için: canvas click ile yerleştir (AR için XR select var)
    renderer.domElement.style.touchAction = "none";
    const onCanvasClick = (ev) => {
      if (!renderer.domElement.contains(ev.target)) return;
      if (reticle.visible && model && !placed) {
        if (!model.parent) scene.add(model);
        model.position.setFromMatrixPosition(reticle.matrix);
        model.visible = true;
        reticle.visible = false;
        placed = true;
      }
    };
    renderer.domElement.addEventListener("click", onCanvasClick);

    // === AR SESSION START ===
    renderer.xr.addEventListener("sessionstart", async () => {
      const session = renderer.xr.getSession();
      if (!session) return;

      overlayRoot.style.pointerEvents = "auto"; // AR jestleri için aç

      const viewerSpace = await session.requestReferenceSpace("viewer");
      hitTestSource = await session.requestHitTestSource({ space: viewerSpace });
      localSpace = await session.requestReferenceSpace("local");

      // XR SELECT (dokun → reticle'a yerleştir)
      const controller = renderer.xr.getController(0);
      scene.add(controller);
      const onSelect = () => {
        if (reticle.visible && model) {
          if (!model.parent) scene.add(model);
          model.position.setFromMatrixPosition(reticle.matrix);
          model.visible = true;
          reticle.visible = false;
          placed = true;
        }
      };
      controller.addEventListener("select", onSelect);
      session.addEventListener(
        "end",
        () => controller.removeEventListener("select", onSelect),
        { once: true }
      );

      // === AR TOUCH GESTURES (pinch/move/rotate) — overlayRoot ÜZERİNDE ===
      let baseDistance = 0;
      let baseRotation = 0;
      let initialScale = 1;
      let initialRotationY = 0;
      let isDragging = false;
      let lastX = 0;
      let lastY = 0;

      const moveFactor = () => {
        const dist = model ? camera.position.distanceTo(model.position) : 2;
        return 0.0007 * dist;
      };

      const onTouchStart = (e) => {
        if (!model || !placed) return; // yerleştirilmeden jest yok
        if (e.touches.length === 1) {
          isDragging = true;
          lastX = e.touches[0].clientX;
          lastY = e.touches[0].clientY;
        } else if (e.touches.length === 2) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          baseDistance = Math.hypot(dx, dy);
          baseRotation = Math.atan2(dy, dx);
          initialScale = model.scale.x;
          initialRotationY = model.rotation.y;
        }
      };

      const onTouchMove = (e) => {
        if (!model || !placed) return;

        if (e.touches.length === 1 && isDragging) {
          const dx = e.touches[0].clientX - lastX;
          const dy = e.touches[0].clientY - lastY;
          lastX = e.touches[0].clientX;
          lastY = e.touches[0].clientY;

          const right = new THREE.Vector3();
          const forward = new THREE.Vector3();
          camera.getWorldDirection(forward);
          forward.y = 0; forward.normalize();
          right.crossVectors(forward, new THREE.Vector3(0, 1, 0)).negate();

          const f = moveFactor();
          model.position.addScaledVector(right, dx * f);
          model.position.addScaledVector(forward, dy * f);
        } else if (e.touches.length === 2) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const newDistance = Math.hypot(dx, dy);
          const newRotation = Math.atan2(dy, dx);

          const scaleFactor = newDistance / baseDistance;
          const s = Math.min(Math.max(initialScale * scaleFactor, 0.01), 5);
          model.scale.set(s, s, s);

          const rotationDelta = newRotation - baseRotation;
          model.rotation.y = initialRotationY + rotationDelta;
        }
      };

      const onTouchEnd = (e) => {
        if (e.touches.length === 0) isDragging = false;
      };

      overlayRoot.addEventListener("touchstart", onTouchStart, { passive: true });
      overlayRoot.addEventListener("touchmove", onTouchMove, { passive: true });
      overlayRoot.addEventListener("touchend", onTouchEnd, { passive: true });

      session.addEventListener(
        "end",
        () => {
          overlayRoot.removeEventListener("touchstart", onTouchStart);
          overlayRoot.removeEventListener("touchmove", onTouchMove);
          overlayRoot.removeEventListener("touchend", onTouchEnd);
        },
        { once: true }
      );
    });

    // === AR SESSION END ===
    renderer.xr.addEventListener("sessionend", () => {
      hitTestSource = null;
      localSpace = null;
      placed = false;

      overlayRoot.style.pointerEvents = "none"; // AR bitti → overlay jestlerini kapat

      // Masaüstü görünümü geri getir
      renderer.setClearColor(0x000000, 1);
      scene.background = new THREE.Color("#000010");
      scene.fog = new THREE.Fog(new THREE.Color(0x001020), 4, 20);

      reticle.visible = false;
      if (model) model.visible = true;

      // XR butonunu yeniden oluştur/geri göster
      if (navigator.xr && navigator.xr.isSessionSupported) {
        navigator.xr.isSessionSupported("immersive-ar").then((supported) => {
          ensureXRButton(!!supported);
        });
      }
    });

    // === RENDER LOOP ===
    renderer.setAnimationLoop((t, frame) => {
      controls.update();

      if (frame && hitTestSource && !placed) {
        const hitTestResults = frame.getHitTestResults(hitTestSource);
        if (hitTestResults.length > 0) {
          const pose = hitTestResults[0].getPose(localSpace);
          reticle.visible = true;
          reticle.matrix.fromArray(pose.transform.matrix);
        } else {
          reticle.visible = false;
        }
      }

      renderer.render(scene, camera);
    });

    // === Desktop model manipulation (XR aktif değilken) ===
    renderer.domElement.addEventListener("contextmenu", (e) => e.preventDefault());
    let isDragModel = false;
    let lastMX = 0,
      lastMY = 0;

    const startModelDrag = (e) => {
      if (renderer.xr.isPresenting) return; // XR'da devre dışı
      if (!model || !placed) return;
      if (e.button === 2 || e.button === 1 || (e.button === 0 && e.shiftKey)) {
        isDragModel = true;
        lastMX = e.clientX;
        lastMY = e.clientY;
        controls.enabled = false;
      }
    };

    const onModelDrag = (e) => {
      if (!isDragModel || !model) return;
      const dx = e.clientX - lastMX;
      const dy = e.clientY - lastMY;
      lastMX = e.clientX;
      lastMY = e.clientY;

      if (e.buttons === 2) {
        // Sağ tuş: döndür (Y) + hafif ileri/geri
        model.rotation.y += dx * 0.005;
        model.position.z += dy * 0.01;
      } else {
        // Orta tuş veya Shift+Sol: XZ düzleminde taşı
        const right = new THREE.Vector3();
        const forward = new THREE.Vector3();
        camera.getWorldDirection(forward);
        forward.y = 0; forward.normalize();
        right.crossVectors(forward, new THREE.Vector3(0, 1, 0)).negate();
        const dist = camera.position.distanceTo(model.position);
        const f = 0.001 * Math.max(1, dist);
        model.position.addScaledVector(right, dx * f);
        model.position.addScaledVector(forward, dy * f);
      }
    };

    const endModelDrag = () => {
      if (!isDragModel) return;
      isDragModel = false;
      controls.enabled = true;
    };

    renderer.domElement.addEventListener("mousedown", startModelDrag);
    window.addEventListener("mousemove", onModelDrag);
    window.addEventListener("mouseup", endModelDrag);

    // Wheel: ölçekle
    renderer.domElement.addEventListener(
      "wheel",
      (e) => {
        if (renderer.xr.isPresenting) return;
        if (!model || !placed) return;
        const s = Math.min(
          Math.max(model.scale.x * (e.deltaY < 0 ? 1.05 : 0.95), 0.01),
          5
        );
        model.scale.set(s, s, s);
      },
      { passive: true }
    );

    // === RESIZE ===
    const resize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      renderer.domElement.removeEventListener("click", onCanvasClick);
      renderer.domElement.removeEventListener("mousedown", startModelDrag);
      window.removeEventListener("mousemove", onModelDrag);
      window.removeEventListener("mouseup", endModelDrag);
      if (socketRef.current) socketRef.current.close();
      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current = null;
      }
      if (mountNode) mountNode.innerHTML = "";
    };
  }, []);

  const metrics = [
    { key: "turbine", label: "⚙️ Türbin Üretim", color: "#00ffff" },
    { key: "solar", label: "☀️ GES Üretim", color: "#ffcc00" },
    { key: "consumption", label: "🔌 İç Tüketim", color: "#ff6699" },
    { key: "net", label: "📈 Net Satış", color: "#00ff66" },
  ];

  return (
    <>
      <div
        ref={mountRef}
        style={{
          position: "fixed",
          inset: 0,
          overflow: "hidden",
          zIndex: 0,
        }}
      />

      {/* HUD */}
      <div
        style={{
          position: "fixed",
          bottom: 96, // butonla çakışmasın diye biraz yukarı
          right: 20,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "10px",
          fontFamily: "monospace",
          fontSize: "15px",
          zIndex: 9999,
          pointerEvents: "none", // kapsayıcı tıklama almaz
        }}
      >
        {metrics.map((m) => (
          <div
            key={m.key}
            onMouseEnter={() => setHoverKey(m.key)}
            onMouseLeave={() => setHoverKey(null)}
            style={{ position: "relative", pointerEvents: "auto" }} // kart etkileşimli
          >
            {/* Hover'da mini çizgi grafik */}
            {hoverKey === m.key && (
              <div
                style={{
                  position: "absolute",
                  bottom: "110%",
                  right: 0,
                  width: "220px",
                  height: "90px",
                  background: "rgba(0,0,0,0.8)",
                  border: `1px solid ${m.color}`,
                  borderRadius: "8px",
                  padding: "4px",
                  boxShadow: `0 0 10px ${m.color}`,
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={hudHistory.map((h, i) => ({ value: h[m.key], index: i }))}
                  >
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={m.color}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <div
              style={{
                color: m.color,
                background: "rgba(0,20,40,0.7)",
                border: `1px solid ${m.color}`,
                borderRadius: "10px",
                padding: "10px 14px",
                textShadow: `0 0 8px ${m.color}`,
                backdropFilter: "blur(4px)",
                transition: "transform 0.2s",
                transform: hoverKey === m.key ? "scale(1.05)" : "scale(1)",
              }}
            >
              {m.label}: {hudData[m.key].toFixed(2)} MW
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
